"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Section } from "@/components/ui/Section";
import { Typography } from "@/components/ui/Typography";
import Link from "next/link";

interface ImageData {
  src: string;
  alt: string;
}

interface SameAppDifferentLooksProps {
  title: string;
  description: React.ReactNode | string;
  images: ImageData[];
  imgBaseUrl?: string;
  className?: string;
  noBorder?: boolean;
}

interface StepData {
  id: number;
  stepNumber: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  linkText?: string;
  linkHref?: string;
}

interface HowToStepsProps {
  title: string;
  steps: StepData[];
  className?: string;

}

export const SameAppDifferentLooks = ({
  title,
  description,
  images,
  imgBaseUrl = "",
  className = "",
  noBorder = false,
}: SameAppDifferentLooksProps) => {
  return (
    <Section>
      <div className="text-center mb-16">
        <Typography variant="h3" className="mb-6">
          {title}
        </Typography>
        <Typography variant="large" className="max-w-4xl mx-auto mb-4">
          {description}
        </Typography>
        <div className="w-12 h-1 rounded-4xl bg-primary mx-auto"></div>
      </div>

      <div
        className={`grid gap-6 ${className} ${
          images.length === 1
            ? "grid-cols-1"
            : images.length === 2
            ? "md:grid-cols-2"
            : images.length === 3
            ? "md:grid-cols-3"
            : "md:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        {images.map((image, index) => {
          const imageSrc = imgBaseUrl ? `${imgBaseUrl}/${image.src}` : image.src;

          return (
            <div key={index} className={` border-black rounded-md overflow-hidden ${noBorder ? "border-0" : "border-2"}`}>
              <Image
                src={imageSrc}
                alt={image.alt}
                title={image.alt}
                width={450}
                height={280}
                className="w-full h-full object-cover "
              />
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export const HowToSteps = ({ title, steps, className = "" }: HowToStepsProps) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const isExternalLink = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  return (
    <Section className={className}>
      {title && (
        <div className="text-center mb-16">
        <Typography variant="h3" className="mb-6">
          {title}
        </Typography>
        <div className="w-12 h-1 rounded-4xl bg-primary mx-auto"></div>
      </div>
      )}

      <div>
        {steps.map((step, index) => (
          <div key={step.id} className="grid lg:grid-cols-2 gap-8 items-center mb-6">
            <div>
              <Image
                src={step.image}
                alt={step.alt}
                title={step.alt}
                width={500}
                height={300}
                className="w-full rounded-md"
              />
            </div>
            <div>
              <Typography variant="h6" as="span" className="mb-6">
                {step.stepNumber}
              </Typography>
              <Typography variant="h4" as="h4" className="mb-2">
                {step.title}
              </Typography>
              <Typography variant="body" className="mb-4">
                {isClient ? (
                  <span dangerouslySetInnerHTML={{ __html: step.description }} />
                ) : (
                  <span>{step.description}</span>
                )}
              </Typography>

              {step.linkText && step.linkHref && (
                <Link
                  href={step.linkHref}
                  className="text-primary underline hover:text-accent hover:underline"
                  {...(isExternalLink(step.linkHref)
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {step.linkText}
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};
